<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  position: {
    type: String,
    default: 'bottom',
    validator: (value) => ['bottom', 'top', 'left', 'right'].includes(value)
  },
  collapsedSize: {
    type: String,
    default: '72px'
  },
  maxSize: {
    type: String,
    default: '90vh'
  }
});

const emit = defineEmits(['update:modelValue']);

const isExpanded = ref(props.modelValue);

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    isExpanded.value = newValue;
  }
);

const toggle = () => {
  isExpanded.value = !isExpanded.value;
  emit('update:modelValue', isExpanded.value);
};

const sheetClasses = computed(() => ['sheet', `sheet--${props.position}`, { 'sheet--expanded': isExpanded.value }]);

const borderRadiusStyle = computed(() => {
  switch (props.position) {
    case 'bottom':
      return '16px 16px 0 0';
    case 'top':
      return '0 0 16px 16px';
    case 'left':
      return '0 16px 16px 0';
    case 'right':
      return '16px 0 0 16px';
    default:
      return '16px 16px 0 0';
  }
});
</script>

<template>
  <div :class="sheetClasses" :style="{ borderRadius: borderRadiusStyle }">
    <!-- Header -->
    <div class="sheet__header" @click="toggle">
      <slot name="header" :is-expanded="isExpanded" :toggle="toggle">
        <div class="d-flex align-center justify-space-between w-100">
          <h3 class="sheet__title">{{ title }}</h3>
          <v-icon :class="{ 'chevron-rotated': isExpanded }">
            {{
              position === 'bottom'
                ? 'mdi-chevron-up'
                : position === 'top'
                  ? 'mdi-chevron-down'
                  : position === 'left'
                    ? 'mdi-chevron-right'
                    : 'mdi-chevron-left'
            }}
          </v-icon>
        </div>
      </slot>
    </div>

    <!-- Content -->
    <div class="sheet__content">
      <slot :is-expanded="isExpanded" :toggle="toggle" />
    </div>
  </div>
</template>

<style scoped>
.sheet {
  position: fixed;
  z-index: 1000;
  background: white;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
}

/* Bottom position */
.sheet--bottom {
  bottom: 0;
  left: 0;
  right: 0;
  max-height: v-bind(maxSize);
  transform: translateY(calc(100% - v-bind(collapsedSize)));
}

.sheet--bottom.sheet--expanded {
  transform: translateY(0);
}

/* Top position */
.sheet--top {
  top: 0;
  left: 0;
  right: 0;
  max-height: v-bind(maxSize);
  transform: translateY(calc(-100% + v-bind(collapsedSize)));
}

.sheet--top.sheet--expanded {
  transform: translateY(0);
}

/* Left position */
.sheet--left {
  top: 0;
  left: 0;
  bottom: 0;
  max-width: v-bind(maxSize);
  transform: translateX(calc(-100% + v-bind(collapsedSize)));
  flex-direction: row;
}

.sheet--left.sheet--expanded {
  transform: translateX(0);
}

/* Right position */
.sheet--right {
  top: 0;
  right: 0;
  bottom: 0;
  max-width: v-bind(maxSize);
  transform: translateX(calc(100% - v-bind(collapsedSize)));
  flex-direction: row;
}

.sheet--right.sheet--expanded {
  transform: translateX(0);
}

.sheet__header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  background: white;
}

.sheet--left .sheet__header,
.sheet--right .sheet__header {
  border-bottom: none;
  border-right: 1px solid #e0e0e0;
  writing-mode: vertical-rl;
}

.sheet__title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #191919;
}

.sheet__content {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  background: white;
}

.chevron-rotated {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.sheet__header :deep(v-icon) {
  transition: transform 0.3s ease;
}
</style>
